<!--
  Thân màn CHI TIẾT hoa hồng một nhân viên — SRS §7.7.
  Tách riêng khỏi ReportDetail.vue để phần hiển thị gọn và dùng lại được.
  Dữ liệu tổng quan / cộng-trừ / nhóm nhận qua props; riêng danh sách hoá đơn
  tự gọi API có phân trang (§7.5 #3) vì một nhân viên có thể hơn 1.200 dòng.
-->
<template>
  <div>
        <!-- Đang tải -->
        <div v-if="onLoading">
          <vcl-table :speed="5" :animate="true" :columns="8"></vcl-table>
        </div>

        <!-- Rỗng / lỗi -->
        <div v-else-if="!overview" class="text-center text-muted py-10">
          Không có dữ liệu hoa hồng của nhân viên trong kỳ đã chọn.
        </div>

        <div v-else>
          <!-- 1. Thẻ tổng quan (§7.7) -->
          <div class="col-md-12 mb-4 pl-0">
            <b-row>
              <b-col md="6">
                <div class="mb-1">
                  <span class="text-muted mr-2">Nhân viên:</span>
                  <span class="font-weight-bold">{{
                    employeeLabel
                  }}</span>
                </div>
                <div class="mb-1">
                  <span class="text-muted mr-2">Chức vụ:</span>
                  <span>{{ overview.jobTitleName || '--' }}</span>
                </div>
              </b-col>
              <b-col md="6">
                <div class="mb-1">
                  <span class="text-muted mr-2">Kỳ báo cáo:</span>
                  <span class="font-weight-bold">{{
                    displayPeriod(period)
                  }}</span>
                </div>
                <div class="mb-1">
                  <span class="text-muted mr-2">{{ scopeLabel }}:</span>
                  <span>{{ scopeName || '--' }}</span>
                </div>
              </b-col>
            </b-row>
          </div>

          <div class="col-md-12 mb-5 pl-0">
            <b-row>
              <b-col
                v-for="card in summaryCards"
                :key="card.key"
                md="3"
                class="mb-3"
              >
                <div
                  class="summary-card"
                  :class="{ 'is-total': card.strong }"
                >
                  <div class="summary-card__label">{{ card.label }}</div>
                  <div
                    class="summary-card__value"
                    :class="amountClass(card.raw)"
                  >
                    {{ card.value }}
                  </div>
                </div>
              </b-col>
            </b-row>
          </div>

          <!-- 2. Khoản cộng / trừ -->
          <h5 class="font-weight-bold mb-3">Khoản cộng / trừ</h5>
          <b-table
            :items="adjustments"
            :fields="adjustmentFields"
            bordered
            hover
            small
            show-empty
            empty-text="Không có khoản cộng / trừ nào trong kỳ"
            class="mb-5"
          >
            <template v-slot:cell(date)="row">
              <span>{{ displayDate(row.item.date) }}</span>
            </template>
            <template v-slot:cell(sourceTypeName)="row">
              <span
                class="label label-inline font-weight-bold"
                :class="sourceTypeVariant(row.item.sourceType)"
                >{{ adjustmentSourceName(row.item) }}</span
              >
            </template>
            <template v-slot:cell(operatorName)="row">
              <span :class="adjustmentOperatorClass(row.item)">{{
                row.item.operatorName || '--'
              }}</span>
            </template>
            <template v-slot:cell(value)="row">
              <span :class="adjustmentOperatorClass(row.item)">{{
                adjustmentValue(row.item)
              }}</span>
            </template>
            <template v-slot:cell(reasonName)="row">
              <span>{{ adjustmentReasonName(row.item) }}</span>
            </template>
            <template v-slot:cell(description)="row">
              <span>{{ row.item.description || '--' }}</span>
            </template>
            <template v-slot:cell(billNumber)="row">
              <span>{{ row.item.billNumber || '--' }}</span>
            </template>
          </b-table>

          <!-- 3. Chi tiết theo nhóm hoa hồng -->
          <h5 class="font-weight-bold mb-3">Chi tiết theo nhóm hoa hồng</h5>
          <div
            v-if="!categoryGroups.length"
            class="text-center text-muted py-5 mb-5 border rounded"
          >
            Không có nhóm hoa hồng nào phát sinh trong kỳ.
          </div>
          <div v-else class="mb-5">
            <div
              v-for="group in categoryGroups"
              :key="group.commissionCategoryId"
              class="category-group mb-2"
            >
              <div
                class="category-group__head"
                @click="toggleGroup(group.commissionCategoryId)"
              >
                <span class="mr-2">
                  <i
                    class="fas"
                    :class="
                      isGroupOpen(group.commissionCategoryId)
                        ? 'fa-chevron-down'
                        : 'fa-chevron-right'
                    "
                  ></i>
                </span>
                <span class="font-weight-bold flex-grow-1">{{
                  group.commissionCategoryName || 'Không xác định'
                }}</span>
                <span class="text-muted mr-4"
                  >SL: {{ displayQuantity(group.quantity) }}</span
                >
                <span
                  class="font-weight-bold"
                  :class="amountClass(group.amount)"
                  >{{ displayAmount(group.amount) }}</span
                >
              </div>
              <b-collapse :visible="isGroupOpen(group.commissionCategoryId)">
                <b-table
                  :items="group.details || []"
                  :fields="categoryDetailFields"
                  bordered
                  small
                  show-empty
                  empty-text="Không có mức thưởng nào khớp"
                  class="mb-0 category-group__table"
                >
                  <template v-slot:cell(rate)="row">
                    <span>{{ displayRate(row.item) }}</span>
                  </template>
                  <template v-slot:cell(quantity)="row">
                    <span>{{ displayQuantity(row.item.quantity) }}</span>
                  </template>
                  <template v-slot:cell(amount)="row">
                    <span :class="amountClass(row.item.amount)">{{
                      displayAmount(row.item.amount)
                    }}</span>
                  </template>
                </b-table>
              </b-collapse>
            </div>
          </div>

          <!-- 4. Danh sách hóa đơn — gọi API riêng, phân trang + tìm kiếm (§7.5 #3) -->
          <h5 class="font-weight-bold mb-3">
            Danh sách hóa đơn
            <span class="text-muted small font-weight-normal">
              ({{ billTotalRow }} dòng)
            </span>
          </h5>
          <b-row class="mb-3">
            <b-col md="3">
              <b-input
                v-model="billFilter.billNumber"
                size="sm"
                placeholder="Mã hóa đơn"
                autocomplete="off"
                @keyup.enter="onBillSearch"
              ></b-input>
            </b-col>
            <b-col md="3">
              <b-input
                v-model="billFilter.productName"
                size="sm"
                placeholder="Tên sản phẩm"
                autocomplete="off"
                @keyup.enter="onBillSearch"
              ></b-input>
            </b-col>
            <b-col md="2">
              <b-input
                v-model="billFilter.commissionCategoryName"
                size="sm"
                placeholder="Nhóm hoa hồng"
                autocomplete="off"
                @keyup.enter="onBillSearch"
              ></b-input>
            </b-col>
            <b-col md="2">
              <b-form-select
                v-model="billFilter.sourceType"
                size="sm"
                :options="sourceTypeOptions"
                value-field="id"
                text-field="name"
              >
                <template v-slot:first>
                  <b-form-select-option :value="null"
                    >-- Nguồn --</b-form-select-option
                  >
                </template>
              </b-form-select>
            </b-col>
            <b-col md="2">
              <b-button
                size="sm"
                variant="primary"
                class="mr-1"
                @click="onBillSearch"
                >Tìm</b-button
              >
              <b-button size="sm" variant="secondary" @click="resetBillFilter"
                >Xóa</b-button
              >
            </b-col>
          </b-row>
          <b-table
            :items="billRows"
            :busy="billLoading"
            :fields="billFields"
            bordered
            hover
            small
            show-empty
            empty-text="Không có hóa đơn nào phát sinh hoa hồng trong kỳ"
            :tbody-tr-class="billRowClass"
          >
            <template v-slot:table-busy>
              <vcl-table :speed="5" :animate="true" :columns="9"></vcl-table>
            </template>
            <template v-slot:cell(billDate)="row">
              <span>{{ displayDate(row.item.billDate) }}</span>
            </template>
            <template v-slot:cell(billNumber)="row">
              <div>{{ row.item.billNumber || '--' }}</div>
              <div
                v-if="row.item.relatedBillNumber"
                class="text-muted small"
              >
                HĐ gốc: {{ row.item.relatedBillNumber }}
              </div>
            </template>
            <template v-slot:cell(sourceTypeName)="row">
              <span
                class="label label-inline font-weight-bold"
                :class="sourceTypeVariant(row.item.sourceType)"
                >{{ sourceTypeName(row.item) }}</span
              >
            </template>
            <template v-slot:cell(productName)="row">
              <span>{{ row.item.productName || '--' }}</span>
            </template>
            <template v-slot:cell(quantity)="row">
              <span>{{ displayQuantity(row.item.quantity) }}</span>
            </template>
            <template v-slot:cell(revenue)="row">
              <span>{{ displayAmount(row.item.revenue) }}</span>
            </template>
            <template v-slot:cell(commissionCategoryName)="row">
              <span>{{ row.item.commissionCategoryName || '--' }}</span>
            </template>
            <template v-slot:cell(commissionProgramName)="row">
              <div>{{ row.item.commissionProgramName || '--' }}</div>
              <div class="text-muted small">{{ displayRate(row.item) }}</div>
            </template>
            <template v-slot:cell(amount)="row">
              <span
                class="font-weight-bold"
                :class="amountClass(signedAmount(row.item))"
                >{{ displayAmount(signedAmount(row.item)) }}</span
              >
            </template>

            <!-- Tổng cộng hoa hồng từ hóa đơn -->
            <template v-slot:custom-foot>
              <tr class="report-table__foot" v-if="billRows.length">
                <td colspan="5" class="text-right font-weight-bold">
                  Tổng cộng
                </td>
                <td class="text-center font-weight-bold">
                  {{ displayQuantity(billTotals.quantity) }}
                </td>
                <td class="text-right font-weight-bold">
                  {{ displayAmount(billTotals.revenue) }}
                </td>
                <td colspan="2"></td>
                <td
                  class="text-right font-weight-bold"
                  :class="amountClass(billTotals.amount)"
                >
                  {{ displayAmount(billTotals.amount) }}
                </td>
              </tr>
            </template>
          </b-table>

          <b-row v-if="billTotalPage > 1">
            <b-col>
              <p class="mt-3 text-dark" style="font-weight: 500">
                Trang {{ billPage }}/{{ billTotalPage }} — tổng
                {{ billTotalRow }} dòng
              </p>
            </b-col>
            <b-col>
              <b-pagination
                v-model="billPage"
                :total-rows="billTotalRow"
                :per-page="BILL_PAGE_LIMIT"
                align="right"
                size="sm"
                class="mt-3"
                @change="onBillPageChange"
              ></b-pagination>
            </b-col>
          </b-row>
        </div>
  </div>
</template>

<script>
import { VclTable } from 'vue-content-loading';
import ApiService from '@/core/services/api.service';
import { convertPrice, makeToastFaile } from '@/utils/common';

const BILL_PAGE_LIMIT = 20;
import {
  COMMISSION_REPORT_TYPE_NAME,
  COMMISSION_ADJUSTMENT_OPERATOR,
  COMMISSION_ADJUSTMENT_OPERATOR_NAME,
  COMMISSION_ADJUSTMENT_AMOUNT_TYPE,
  COMMISSION_ADJUSTMENT_AMOUNT_TYPE_SIGN,
  COMMISSION_ADJUSTMENT_REASON_NAME,
  COMMISSION_SOURCE_TYPE_NAME,
  toOptionList,
} from './commission.constants';
import commissionReportMixin, { thStyle } from './commissionReport.mixin';

export default {
  components: {
    VclTable,
  },
  mixins: [commissionReportMixin],
  props: {
    onLoading: { type: Boolean, default: false },
    overview: { type: Object, default: null },
    period: { type: Object, default: null },
    store: { type: Object, default: null },
    departmentName: { type: String, default: '' },
    adjustments: { type: Array, default: () => [] },
    categoryGroups: { type: Array, default: () => [] },
    /** Tham số để tự gọi API danh sách hoá đơn (§7.5 #3) */
    billQuery: { type: Object, default: null },
  },
  data() {
    return {
      BILL_PAGE_LIMIT,
      billRows: [],
      billLoading: false,
      sourceTypeOptions: toOptionList(COMMISSION_SOURCE_TYPE_NAME),
      billFilter: {
        billNumber: '',
        productName: '',
        commissionCategoryName: '',
        sourceType: null,
      },
      billPage: 1,
      billTotalRow: 0,
      billTotalPage: 1,
      billApiTotals: { quantity: 0, revenue: 0, amount: 0 },

      openGroupIds: [],
      adjustmentFields: [
        {
          key: 'date',
          label: 'Ngày',
          thStyle: thStyle('9%'),
          tdClass: 'text-center',
        },
        {
          key: 'sourceTypeName',
          label: 'Nguồn',
          thStyle: thStyle('10%'),
          tdClass: 'text-center',
        },
        {
          key: 'operatorName',
          label: 'Toán tử',
          thStyle: thStyle('9%'),
          tdClass: 'text-center',
        },
        {
          key: 'value',
          label: 'Giá trị',
          thStyle: thStyle('12%'),
          tdClass: 'text-right',
        },
        {
          key: 'reasonName',
          label: 'Lý do',
          thStyle: thStyle('18%'),
        },
        {
          key: 'description',
          label: 'Mô tả',
          thStyle: thStyle('30%'),
        },
        {
          key: 'billNumber',
          label: 'Mã HĐ',
          thStyle: thStyle('10%'),
        },
      ],
      categoryDetailFields: [
        {
          key: 'commissionProgramName',
          label: 'Chương trình',
          thStyle: thStyle('45%'),
        },
        {
          key: 'rate',
          label: 'Mức thưởng',
          thStyle: thStyle('20%'),
          tdClass: 'text-center',
        },
        {
          key: 'quantity',
          label: 'Số lượng',
          thStyle: thStyle('15%'),
          tdClass: 'text-center',
        },
        {
          key: 'amount',
          label: 'Thành tiền',
          thStyle: thStyle('20%'),
          tdClass: 'text-right',
        },
      ],
      billFields: [
        {
          key: 'billDate',
          label: 'Ngày',
          thStyle: thStyle('8%'),
          tdClass: 'text-center',
        },
        {
          key: 'billNumber',
          label: 'Mã HĐ',
          thStyle: thStyle('10%'),
        },
        {
          key: 'sourceTypeName',
          label: 'Nguồn',
          thStyle: thStyle('9%'),
          tdClass: 'text-center',
        },
        {
          key: 'jobTitleName',
          label: 'Vai trò',
          thStyle: thStyle('10%'),
          tdClass: 'text-center',
        },
        {
          key: 'productName',
          label: 'Sản phẩm',
          thStyle: thStyle('17%'),
        },
        {
          key: 'quantity',
          label: 'SL',
          thStyle: thStyle('5%'),
          tdClass: 'text-center',
        },
        {
          key: 'revenue',
          label: 'Doanh thu',
          thStyle: thStyle('10%'),
          tdClass: 'text-right',
        },
        {
          key: 'commissionCategoryName',
          label: 'Nhóm HH',
          thStyle: thStyle('11%'),
        },
        {
          key: 'commissionProgramName',
          label: 'Chương trình',
          thStyle: thStyle('10%'),
        },
        {
          key: 'amount',
          label: 'Thành tiền',
          thStyle: thStyle('10%'),
          tdClass: 'text-right',
        },
      ],
    };
  },
  computed: {
    pageTitle() {
      const base =
        COMMISSION_REPORT_TYPE_NAME[this.reportType] || 'Báo cáo hoa hồng';
      return `Chi tiết hoa hồng nhân viên — ${base}`;
    },
    employeeLabel() {
      if (!this.overview) {
        return '';
      }
      return this.overview.employeeCode
        ? `${this.overview.employeeName} (${this.overview.employeeCode})`
        : this.overview.employeeName || '--';
    },
    scopeName() {
      return this.displayScopeName({
        store: this.store,
        departmentName: this.departmentName,
      });
    },
    /** Bốn thành phần + Tổng thưởng (§7.4) — overview đã cộng dấu sẵn */
    summaryCards() {
      const overview = this.overview || {};
      return [
        {
          key: 'commissionAmount',
          label: 'Hoa hồng',
          value: this.displayAmount(overview.commissionAmount),
          raw: overview.commissionAmount,
        },
        {
          key: 'adhocAmount',
          label: 'AdHoc',
          value: this.displayAmount(overview.adhocAmount),
          raw: overview.adhocAmount,
        },
        {
          key: 'bonusAmount',
          label: 'Cộng khác',
          value: this.displayAmount(overview.bonusAmount),
          raw: overview.bonusAmount,
        },
        {
          key: 'minusAmount',
          label: 'Trừ khác',
          value: this.displayAmount(overview.minusAmount),
          raw: overview.minusAmount,
        },
        {
          key: 'totalAmount',
          label: 'Tổng thưởng',
          value: this.displayAmount(overview.totalAmount),
          raw: overview.totalAmount,
          strong: true,
        },
      ];
    },
    /** Tổng cuối bảng hóa đơn — amount phải nhân direction trước khi cộng (§7.4) */
    /**
     * Tổng của TOÀN BỘ kết quả lọc (API trả về), KHÔNG phải tổng trang đang xem —
     * nếu cộng theo trang thì đứng ở trang 1 sẽ thấy tổng sai.
     */
    billTotals() {
      return this.billApiTotals;
    },
  },
  watch: {
    // Đổi nhân viên / đổi kỳ -> nạp lại danh sách hoá đơn từ đầu
    billQuery: {
      handler() {
        this.resetBillFilterState();
        this.billPage = 1;
        this.fetchBills();
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    /* ------------------------ Danh sách hoá đơn (§7.5 #3) ------------------ */
    /** Bấm Tìm (hoặc Enter trong ô nhập) mới gọi API — không tự tìm khi đang gõ */
    onBillSearch() {
      this.billPage = 1;
      this.fetchBills();
    },
    resetBillFilterState() {
      this.billFilter = {
        billNumber: '',
        productName: '',
        commissionCategoryName: '',
        sourceType: null,
      };
    },
    resetBillFilter() {
      this.resetBillFilterState();
      this.billPage = 1;
      this.fetchBills();
    },
    onBillPageChange(page) {
      this.billPage = page;
      this.$nextTick(() => this.fetchBills());
    },
    /** Gọi API riêng cho danh sách hoá đơn — không nhét hết vào /details */
    fetchBills() {
      if (!this.billQuery || !this.billQuery.employeeId) {
        this.billRows = [];
        this.billTotalRow = 0;
        this.billTotalPage = 1;
        this.billApiTotals = { quantity: 0, revenue: 0, amount: 0 };
        return;
      }
      this.billLoading = true;
      const params = {
        ...this.billQuery,
        page: this.billPage,
        limit: BILL_PAGE_LIMIT,
      };
      // Mỗi tiêu chí gửi một trường riêng — BE so đúng cột đó, không quét OR
      Object.keys(this.billFilter).forEach((key) => {
        const value = this.billFilter[key];
        if (value !== null && value !== '') {
          params[key] = value;
        }
      });
      ApiService.setHeader();
      ApiService.query('commission-reports/bills', { params })
        .then(({ data }) => {
          const result = data.data || {};
          this.billRows = result.data || [];
          this.billTotalRow = result.totalRow || 0;
          this.billTotalPage = result.totalPage || 1;
          const totals = result.totals || {};
          this.billApiTotals = {
            quantity: totals.quantity || 0,
            revenue: totals.revenue || 0,
            amount: totals.amount || 0,
          };
        })
        .catch((error) => {
          this.billRows = [];
          this.billTotalRow = 0;
          this.billTotalPage = 1;
          const message =
            error.response && error.response.data
              ? error.response.data.message
              : 'Không tải được danh sách hóa đơn!';
          makeToastFaile(message);
        })
        .finally(() => {
          this.billLoading = false;
        });
    },

    backQuery() {
      return { type: this.reportType };
    },

    /* --------------------------- Nhóm hoa hồng --------------------------- */
    isGroupOpen(categoryId) {
      return this.openGroupIds.indexOf(categoryId) > -1;
    },
    toggleGroup(categoryId) {
      const index = this.openGroupIds.indexOf(categoryId);
      if (index > -1) {
        this.openGroupIds.splice(index, 1);
      } else {
        this.openGroupIds.push(categoryId);
      }
    },
    /** Mức thưởng cấu hình: tiền hay % (dùng chung cho nhóm và bảng hóa đơn) */
    displayRate(item) {
      if (!item || item.rateValue === null || item.rateValue === undefined) {
        return '--';
      }
      const value = Number(item.rateValue) || 0;
      return Number(item.rateType) === COMMISSION_ADJUSTMENT_AMOUNT_TYPE.PERCENT
        ? `${convertPrice(value)}%`
        : this.displayAmount(value);
    },

    /* ---------------------------- Khoản cộng/trừ ------------------------- */
    adjustmentSourceName(item) {
      return (
        item.sourceTypeName ||
        COMMISSION_SOURCE_TYPE_NAME[item.sourceType] ||
        '--'
      );
    },
    adjustmentReasonName(item) {
      return (
        item.reasonName ||
        COMMISSION_ADJUSTMENT_REASON_NAME[item.reasonType] ||
        '--'
      );
    },
    adjustmentOperatorClass(item) {
      return Number(item.operatorType) === COMMISSION_ADJUSTMENT_OPERATOR.MINUS
        ? 'text-danger font-weight-bold'
        : 'text-success font-weight-bold';
    },
    /**
     * Giá trị bút toán: hiện giá trị cấu hình (tiền / %) và số tiền quy đổi
     * khi cấu hình là phần trăm.
     */
    adjustmentValue(item) {
      const amountType = Number(item.amountType);
      const sign =
        COMMISSION_ADJUSTMENT_AMOUNT_TYPE_SIGN[amountType] ||
        COMMISSION_ADJUSTMENT_AMOUNT_TYPE_SIGN[
          COMMISSION_ADJUSTMENT_AMOUNT_TYPE.AMOUNT
        ];
      const amount = this.displayAmount(item.amount);
      if (amountType === COMMISSION_ADJUSTMENT_AMOUNT_TYPE.PERCENT) {
        return `${convertPrice(Number(item.rateValue) || 0)}${sign} = ${amount}`;
      }
      return amount;
    },
    operatorNameOf(item) {
      return (
        item.operatorName ||
        COMMISSION_ADJUSTMENT_OPERATOR_NAME[item.operatorType] ||
        '--'
      );
    },

    /* ------------------------------ Hóa đơn ------------------------------ */
    billRowClass(item) {
      return item && this.isMinusRow(item) ? 'table-danger-soft' : '';
    },

    /* ---------------------------- Tải dữ liệu ---------------------------- */
  },
};
</script>

<style scoped>
.table th,
.table td {
  padding: 0.75rem;
  vertical-align: middle;
  border-top: 1px solid #ecf0f3;
}

.summary-card {
  border: 1px solid #ecf0f3;
  border-radius: 0.42rem;
  padding: 0.85rem 1rem;
  height: 100%;
}

.summary-card__label {
  color: #7e8299;
  font-size: 0.8rem;
  margin-bottom: 0.35rem;
}

.summary-card__value {
  font-size: 1.15rem;
  font-weight: 600;
  color: #181c32;
}

/* Khối nhóm hoa hồng: đầu nhóm bấm để bung/thu, bảng con bỏ viền hai bên */
.category-group {
  border: 1px solid #ecf0f3;
  border-radius: 0.42rem;
  overflow: hidden;
}

.category-group__head {
  display: flex;
  align-items: center;
  padding: 0.65rem 1rem;
  background-color: #f3f6f9;
  cursor: pointer;
}

.category-group__table >>> th,
.category-group__table >>> td {
  border-left: none;
  border-right: none;
}

.report-table__foot > td {
  background-color: #f3f6f9;
}

.table >>> tr.table-danger-soft > td {
  background-color: #fff5f5;
  color: #d33;
}
</style>
